import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { checkEmptyInputValue } from '@pages/AdditionalServices/tabs/DeliveryForm/helpers';
import { RouteWidgetBaseParameters } from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import useDelivery from '@pages/AdditionalServices/tabs/DeliveryForm/useDelivery';
import { cargoConverterService } from '@services/requests/routeCalculator/cargoConverterService';
import { SelectCargoParametersQueryResponse } from '@services/requests/routeCalculator/cargoConverterService/SelectCargoParametersQuery';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import RoutesCalculationWidgetStyledContainer from '../../StyledComponents';
import CargoSelectionList from '../CargoSelectionList';
import { CargoSelectionResult } from '../CargoSelectionResult';
import { CargoSelectionType, SelectionCustomCargoParameters } from '../types';
import { DEFAULT_SELECTION_CARGO_PARAMETERS_STATE, SELECTION_CARGO_TITLE } from './constants';
import {
  CargoSelectionDialogStyledContainer,
  CargoSelectionStyledContainer,
} from './StyledComponent';

interface CargoSelectionDialogProps {
  isDialogOpen: boolean;
  BaseData: RouteWidgetBaseParameters;
  isActionsDisabled: boolean;
  mainLanguage: string; // Текущий основной язык
  cargoSelectionType: CargoSelectionType;
  onCloseDialog: () => void;
}

function CargoSelectionDialog({
  isDialogOpen,
  BaseData,
  isActionsDisabled,
  mainLanguage,
  cargoSelectionType,
  onCloseDialog,
}: CargoSelectionDialogProps) {
  const [selectionCargoParamaters, setSelectionCargoParameters] = useState<
    SelectionCustomCargoParameters[]
  >(DEFAULT_SELECTION_CARGO_PARAMETERS_STATE);
  const [selectedParams, setSelectedParams] = useState<
    SelectCargoParametersQueryResponse['data'] | null
  >(null);
  const [isSelectionLoading, setIsSelectionLoading] = useState<boolean>(false);
  const [isSelectingError, setIsSelectingError] = useState<boolean>(false);

  useEffect(() => {
    setSelectedParams(null);
    setIsSelectingError(false);
  }, [selectionCargoParamaters]);

  const { useRouteParams } = useDelivery();
  const [routeParams] = useRouteParams().useState();

  const { t } = useTranslation('Common');

  const isCargoFieldsFilled = useMemo(
    () =>
      selectionCargoParamaters
        .map((parameters) => {
          switch (parameters.customCargoType) {
            case 'base':
              return [
                checkEmptyInputValue(parameters.baseParameters.height),
                checkEmptyInputValue(parameters.baseParameters.length),
                checkEmptyInputValue(parameters.baseParameters.quantity),
                checkEmptyInputValue(parameters.baseParameters.weight),
                checkEmptyInputValue(parameters.baseParameters.width),
              ];
            case 'pallet':
              const pallet = BaseData.units.find((u) => u.id === parameters.palletParameters.type);
              return [
                checkEmptyInputValue(parameters.palletParameters.normativeHeightOfStacking),
                checkEmptyInputValue(parameters.palletParameters.weight),
                checkEmptyInputValue(parameters.palletParameters.quantity),
                !pallet,
              ];
            case 'volume':
              return [
                checkEmptyInputValue(parameters.volumeParameters.volume),
                checkEmptyInputValue(parameters.volumeParameters.weight),
              ];
            default:
              return [];
          }
        })
        .flat()
        .every((value) => !value),
    [selectionCargoParamaters]
  );

  /**
   * Удаление одного из параметров подбора
   * @param index
   */
  const deleteSelectionCargoParametersHanndler = (index: number) => {
    setSelectionCargoParameters((prevState) => prevState.filter((_, i) => i !== index));
  };

  /**
   * Обработка изменения одного из параметров подбора
   * @param cargoParameters
   * @param index
   */
  const changeSelectionCargoParametersHandler = (
    cargoParameters: SelectionCustomCargoParameters,
    index: number
  ) => {
    setSelectionCargoParameters((prevState) => {
      const updatedCargoParameters = [...prevState];
      updatedCargoParameters[index] = cargoParameters;

      return updatedCargoParameters;
    });
  };

  /**
   * Обработка добавления нового параметра подбора
   */
  const addSelectionCargoParametersHandler = () => {
    const defaultPalletType = BaseData.units.filter((u) => u.unit_group === '4')[0].id || '0';
    setSelectionCargoParameters((prevState) => [
      ...prevState,
      {
        customCargoType: 'base',
        parametersCargoCalcType: 'volume',
        baseParameters: {
          quantity: 1,
          length: '1',
          width: '1',
          height: '1',
          weight: '10',
        },
        palletParameters: {
          type: defaultPalletType,
          quantity: 1,
          normativeHeightOfStacking: '1',
          weight: 1000,
        },
        volumeParameters: {
          volume: '1',
          weight: '10',
        },
      },
    ]);
  };

  const selectCargoParametersHandler = async () => {
    if (isActionsDisabled || !isCargoFieldsFilled) {
      return;
    }

    setIsSelectionLoading(true);
    const selectCargoData = await cargoConverterService().GetSelectedParams(
      selectionCargoParamaters,
      routeParams.cargoType
    );

    if (!selectCargoData || (!selectCargoData.conversationCargos && !!selectCargoData.errorMsg)) {
      setIsSelectingError(true);
      setIsSelectionLoading(false);
      return;
    }
    setIsSelectionLoading(false);
    setSelectedParams(selectCargoData);
  };

  return (
    <CargoSelectionDialogStyledContainer
      fullWidth
      open={isDialogOpen}
      scroll={'body'}
      maxWidth="xl"
    >
      <RoutesCalculationWidgetStyledContainer>
        <Box className="close-button">
          <IconButton onClick={onCloseDialog}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </Box>
        <CargoSelectionStyledContainer>
          <Typography variant={'h2'}>
            {t('Common:CargoSelection.SelectionDialog.Title')}{' '}
            {SELECTION_CARGO_TITLE[cargoSelectionType]}
          </Typography>
          <div>
            <Alert color={'info'} severity={'info'}>
              <p>
                <Trans
                  i18nKey="CargoSelection.SelectionDialog.InfoText"
                  components={[<b key="bold" />, <br key="br" />]}
                />
              </p>
            </Alert>
          </div>
          <Button
            onClick={selectCargoParametersHandler}
            className={'pallet-edit-button'}
            variant={'contained'}
            color={'primary'}
            fullWidth={true}
            size="medium"
            disabled={isActionsDisabled || !isCargoFieldsFilled}
          >
            {t('Common:CargoSelection.SelectionDialog.RunSelectionButton')}
          </Button>
          <CargoSelectionList
            BaseData={BaseData}
            isActionsDisabled={isActionsDisabled}
            mainLanguage={mainLanguage}
            selectionCargoParamaters={selectionCargoParamaters}
            onAddSelectionCargoParameters={addSelectionCargoParametersHandler}
            onChangeSelectionCargoParameters={changeSelectionCargoParametersHandler}
            onDeleteSelectionCargoParameters={deleteSelectionCargoParametersHanndler}
          />
          <CargoSelectionResult
            isSelectionLoading={isSelectionLoading}
            isSelectingError={isSelectingError}
            selectedParams={selectedParams}
            isActionsDisabled={isActionsDisabled}
            BaseData={BaseData}
            mainLanguage={mainLanguage}
            onCloseDialog={onCloseDialog}
          />
        </CargoSelectionStyledContainer>
      </RoutesCalculationWidgetStyledContainer>
    </CargoSelectionDialogStyledContainer>
  );
}

export { CargoSelectionDialog };
